import { Alert, Button, message, Modal } from 'antd'
import { DaoProjectItemType, DaoProposalType } from 'Modules'
import { useState } from 'react'
import { useAccount } from 'wagmi'

import { AppAdmin } from 'api/app-admin'
import { DAO_MESSAGES, DaoProposalStatusEnum, DaoSellingVersionEnum } from 'constants/dao-voting'
import { useDaoVotingContract } from 'hooks/dao-voting/use-dao-voting-contract'
import { daoTxErrorMessage, usePreparedTx } from 'hooks/dao-voting/use-prepared-tx'
import { daoApiErrorMessage, getProposalDisplayName } from 'modules/dao-voting/utils'
import { onCloseModalProcessing, onOpenModalProcessing } from 'stores/modal.store'
import { logError } from 'utils/logger'

interface DeleteProposalModalProps {
  open: boolean
  project: DaoProjectItemType
  proposal?: DaoProposalType | null
  onClose: () => void
  onSuccess: () => void
}

/**
 * Delete Proposal confirmation (SRS 5.4) - only Draft/Scheduled can be deleted;
 * a Deleting one (PTO ERC-3643, cancel tx not confirmed) can be retried
 */
const DeleteProposalModal = ({
  open,
  project,
  proposal,
  onClose,
  onSuccess,
}: DeleteProposalModalProps) => {
  const [submitting, setSubmitting] = useState(false)

  const { address } = useAccount()
  const { deleteProposalOnChain } = useDaoVotingContract()
  const { sendPreparedTx } = usePreparedTx()

  const handleDelete = async () => {
    if (!proposal) return

    try {
      setSubmitting(true)

      const isDeleting = proposal.status === DaoProposalStatusEnum.DELETING

      // Scheduled proposal lives on-chain (Pending): the on-chain delete MUST
      // succeed first (frees the collection), only then the BE record is
      // removed (DAO_Usage_Guide 3.2). Missing on-chain id = hard stop.
      if (proposal.status === DaoProposalStatusEnum.SCHEDULED || isDeleting) {
        if (!address) {
          message.error('Please connect wallet!')
          return
        }
        // PTO ERC-3643: the BE moves the proposal to Deleting and prepares
        // PTOGovernor.cancel; the row is removed once the chain confirms.
        // Retrying a Deleting one returns a fresh cancel call.
        if (isDeleting || project.sellingVersion === DaoSellingVersionEnum.ERC3643) {
          const res = await AppAdmin.deleteDaoVotingProposal(proposal._id)
          const deleteCall = res.data?.data?.deleteCall
          if (deleteCall) {
            try {
              onOpenModalProcessing()
              await sendPreparedTx(address, deleteCall)
            } catch (chainError) {
              logError('Error deleting proposal on-chain: ', chainError)
              message.error(daoTxErrorMessage(chainError) || 'Failed to delete proposal on-chain')
              // the proposal is Deleting now - reload so the row offers a retry
              onSuccess()
              onClose()
              return
            } finally {
              onCloseModalProcessing()
            }
          }

          message.success(DAO_MESSAGES.S04)
          onSuccess()
          onClose()
          return
        }

        const detail = (await AppAdmin.getDaoVotingProposalDetail(proposal._id)).data?.data
        const onChainProposalId = detail?.onChainProposalId
        const contractAddress = detail?.contractAddress || proposal.contractAddress

        if (!onChainProposalId || !contractAddress) {
          message.error('Cannot delete: on-chain proposal id is not available')
          return
        }

        try {
          onOpenModalProcessing()
          await deleteProposalOnChain(address, { contractAddress, onChainProposalId })
        } catch (chainError) {
          logError('Error deleting proposal on-chain: ', chainError)
          message.error(daoTxErrorMessage(chainError) || 'Failed to delete proposal on-chain')
          return
        } finally {
          onCloseModalProcessing()
        }

        // on-chain delete is the whole job here - the BE syncs the record
        // from the ProposalDeleted event, no BE delete call needed
        message.success(DAO_MESSAGES.S04)
        onSuccess()
        onClose()
        return
      }

      // Draft / Publishing: nothing on-chain - remove the BE record directly
      await AppAdmin.deleteDaoVotingProposal(proposal._id)
      message.success(DAO_MESSAGES.S04)
      onSuccess()
      onClose()
    } catch (error: any) {
      logError('Error deleting proposal: ', error)
      message.error(daoApiErrorMessage(error) || 'Failed to delete proposal')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Modal
      title="Delete Proposal"
      open={open}
      onCancel={onClose}
      maskClosable={false}
      width={445}
      footer={[
        <Button key="cancel" onClick={onClose}>
          Cancel
        </Button>,
        <Button key="delete" danger type="primary" loading={submitting} onClick={handleDelete}>
          Delete Proposal
        </Button>,
      ]}
    >
      <p>
        You are about to delete <b>{proposal ? getProposalDisplayName(project, proposal) : ''}</b>.
      </p>
      <Alert
        type="error"
        message="This proposal has not started voting yet. Deleting it removes it permanently and cannot be undone."
      />
    </Modal>
  )
}

export default DeleteProposalModal
